import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, FloatingLabel, Form, Modal, Spinner } from 'react-bootstrap';
import Lottie from 'lottie-react';
import PropTypes from 'prop-types';

// Components
import Alert from '../../../utils/Alert';

// Actions
import { global_config_field_create, global_config_field_update, set_global_config_group_id } from '../../../../actions/globalActions';

// Utils
import isEmpty from '../../../../utils/isEmpty';
import errorExists from '../../../../utils/errorExists';

// Icons
import checkAnimation from '../../../../static/icons/checkAnimation.json';

const FieldForm = (props) => {
	const { show, onHide, filters, update, fieldToUpdate } = props;

	const dispatch = useDispatch();

	const {
		global_config_fields_create_loading,
		global_config_fields_create_success,
		global_config_group_id,
		global_errors
	} = useSelector(state => state.global);

	const initialAlert = { display: false, type: '', msg: '' };
	const [alert, setAlert] = useState(initialAlert);

	const initialFieldInfo = { label: '', name: '', description: '' };
	const [fieldInfo, setFieldInfo] = useState(initialFieldInfo);

	const closeModalRef = useRef();
	const lottieRef	= useRef();

	useEffect(() => {
		if (!isEmpty(fieldToUpdate)) {
			setFieldInfo({
				label: fieldToUpdate.label,
				name: fieldToUpdate.name,
				description: fieldToUpdate.description
			});
		}
	}, [fieldToUpdate]);

	const onChange = (e, name) => {
		setFieldInfo({ ...fieldInfo, [name]: e.target.value });
	}

	const closeModal = () => {
		onHide();
		setFieldInfo(initialFieldInfo);
		dispatch(set_global_config_group_id(null));
	}

	const validateFieldInfo = fieldInfo.label === '' || fieldInfo.name === '' || fieldInfo.description === '';

	const createGlobalConfig = () => {		
		if (validateFieldInfo) {
			setAlert({ display: true, type: 'danger', msg: 'Missing fields!' });
			return;
		}
		setAlert(initialAlert);

		const fieldBody = {
			...filters,
			...fieldInfo,
			group: global_config_group_id
		}
		dispatch(global_config_field_create(fieldBody, closeModalRef));
	}

	const updateGlobalConfig = () => {		
		if (validateFieldInfo) {
			setAlert({ display: true, type: 'danger', msg: 'Missing fields!' });
			return;
		}
		setAlert(initialAlert);

		dispatch(global_config_field_update(fieldToUpdate?._id?.$oid, fieldInfo, closeModalRef, global_config_group_id));
	}

	return (
		<Modal
			show={show}
			onHide={closeModal}
			backdrop='static'
			keyboard={false}
			size='lg'
			aria-labelledby='contained-modal-title-vcenter'
			centered
		>
			<Modal.Header closeButton>
				<Modal.Title>{update ? 'Update Field' : 'Create Field'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{alert.display && <Alert type={alert.type} msg={alert.msg} />}
				
				{errorExists(global_errors, 'global_config_field_create') &&
					<Alert type='danger' msg={global_errors['global_config_field_create'].msg} />
				}

				<Form>
					<FloatingLabel
						controlId='floatingInput'
						label='Label'
						className='mb-3'
					>
						<Form.Control placeholder='Label' value={fieldInfo.label} onChange={(e) => onChange(e, 'label')} />
					</FloatingLabel>
					<FloatingLabel
						controlId='floatingInput'
						label='Value'
						className='mb-3'
					>
						<Form.Control placeholder='Name' value={fieldInfo.name} onChange={(e) => onChange(e, 'name')} />
					</FloatingLabel>
					<FloatingLabel
						controlId='floatingInput'
						label='Description'
						className='mb-3'
					>
						<Form.Control as='textarea' placeholder='Description' value={fieldInfo.description} onChange={(e) => onChange(e, 'description')} />
					</FloatingLabel>
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button variant='outline-secondary' onClick={closeModal} ref={closeModalRef}>Cancel</Button>
				<Button 
					className={global_config_fields_create_success ? 'success-btn' : 'submit-btn'}
					onClick={update ? updateGlobalConfig : createGlobalConfig}
				>
					{global_config_fields_create_success
						?	<Lottie
								loop={false}
								lottieRef={lottieRef}
								animationData={checkAnimation}
								style={{ height: '24px' }}
							/>
						:	global_config_fields_create_loading
							?	<Fragment>
									<Spinner className='me-2' as='span' animation='border' size='sm' role='status' aria-hidden='true' />
									{update ? 'Updating' : 'Creating'}
								</Fragment>
							:	update ? 'Update' : 'Create'
					}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

FieldForm.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	filters: PropTypes.object.isRequired,
	update: PropTypes.bool.isRequired,
	fieldToUpdate: PropTypes.object.isRequired
}

export default FieldForm;
